import React, { useEffect, useState, useRef } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import {
  format,
  parse,
  startOfWeek as dfnsStartOfWeek,
  getDay,
  addMonths,
  subMonths,
} from "date-fns";
import esES from "date-fns/locale/es";
import { Global } from "../helpers/Global";
import Select from "react-select";

const DragAndDropCalendar = withDragAndDrop(Calendar);

/* ========= Helpers 100% locales (sin UTC) ========= */
function fechaLocalYMD(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = (`0${d.getMonth() + 1}`).slice(-2);
  const day = (`0${d.getDate()}`).slice(-2);
  return `${y}-${m}-${day}`;
}
// Construye Date local a partir de "YYYY-MM-DD" + "HH:mm"
function mkLocalDateTime(ymd, hm) {
  const [y, m, d] = ymd.split("-").map(n => parseInt(n, 10));
  const [hh, mm] = (hm || "00:00").split(":").map(n => parseInt(n, 10));
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}
function startOfDayLocal(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function endOfDayLocal(d)   { const x = new Date(d); x.setHours(23,59,59,999); return x; }

/* ========= Localizer correcto (firma startOfWeek con date) ========= */
const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...(options || {}), locale: esES }),
  parse: (value, formatStr, options) =>
    parse(value, formatStr, new Date(), { ...(options || {}), locale: esES }),
  startOfWeek: (date, options) =>
    dfnsStartOfWeek(date, { weekStartsOn: 1, locale: esES, ...(options || {}) }),
  getDay,
  locales: { es: esES },
});

const CustomEvent = ({ event }) => (
  <div>
    <div style={{ fontWeight: 600 }}>{event.title}</div>
    {event.descripcion && (
      <div style={{ fontSize: "0.97em", color: "#333" }}>{event.descripcion}</div>
    )}
  </div>
);

export const CalendarioAdmin = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState("");
  const [todosLosTurnos, setTodosLosTurnos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [plantillas, setPlantillas] = useState([]);

  // Modal y slot
  const [modalAbierto, setModalAbierto] = useState(false);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null); // "YYYY-MM-DD"
  const [turnoEditar, setTurnoEditar] = useState(null);
  const [slotSeleccionado, setSlotSeleccionado] = useState(null);

  // Ventana corredera de 7 meses
  const [rangoCargado, setRangoCargado] = useState(() => {
    const ahora = new Date();
    const desde = subMonths(new Date(ahora.getFullYear(), ahora.getMonth(), 1), 3);
    const hasta = addMonths(new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0), 3);
    return { desde: startOfDayLocal(desde), hasta: endOfDayLocal(hasta) };
  });
  const [rangoVisible, setRangoVisible] = useState(() => {
    const hoy = new Date();
    const desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return { desde: startOfDayLocal(desde), hasta: endOfDayLocal(hasta) };
  });

  const calendarRef = useRef();
  const [vistaActual, setVistaActual] = useState("week");

  // Ajusta rango visible al cambiar a "day"
  useEffect(() => {
    if (vistaActual === "day") {
      const visibleDate = calendarRef.current?.state?.currentDate || new Date();
      setRangoVisible({
        desde: startOfDayLocal(visibleDate),
        hasta: endOfDayLocal(visibleDate),
      });
    }
  }, [vistaActual]);

  // === Fetch de usuarios y plantillas ===
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(Global.url + "usuarios", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        setUsuarios(data);
        if (data.length > 0 && !usuarioId) setUsuarioId(data[0].id);
      } catch {
        setMensaje("No se pudieron cargar los usuarios.");
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(Global.url + "turnos/plantillas", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        setPlantillas(data);
      } catch {}
    })();
  }, []);

  // === Fetch de turnos para la ventana actual (YMD local) ===
  const fetchTurnos = (desde, hasta, usuarioIdFetch) => {
    setLoading(true);
    const ymdDesde = fechaLocalYMD(startOfDayLocal(desde));
    const ymdHasta = fechaLocalYMD(endOfDayLocal(hasta));
    fetch(
      Global.url +
        `turnos?usuario_id=${usuarioIdFetch}&desde=${ymdDesde}&hasta=${ymdHasta}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setTodosLosTurnos(
          data.map((turno) => {
            let plantillaNombre = "";
            if (turno.plantilla_id && plantillas.length > 0) {
              const plantilla = plantillas.find(
                (p) => String(p.id) === String(turno.plantilla_id)
              );
              if (plantilla) plantillaNombre = plantilla.nombre;
            }
            return {
              id: turno.id,
              title:
                plantillaNombre ||
                (turno.tipo === "ausencia"
                  ? turno.descripcion || "Ausencia"
                  : "Turno laboral"),
              start: new Date(turno.inicio), // backend ya devuelve timestamp sin tz
              end: new Date(turno.fin),
              allDay: false,
              activo: turno.activo,
              tipo: turno.tipo,
              descripcion: turno.descripcion,
              plantilla_id: turno.plantilla_id,
            };
          })
        );
        setRangoCargado({
          desde: startOfDayLocal(desde),
          hasta: endOfDayLocal(hasta),
        });
      })
      .catch(() => setMensaje("Error de conexión"))
      .finally(() => setLoading(false));
  };

  // Recarga turnos cuando cambia usuario / plantillas
  useEffect(() => {
    if (!usuarioId) return;
    fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
    // eslint-disable-next-line
  }, [usuarioId, plantillas]);

  // Cambios de rango del calendario (normalizados a día completo local)
  const handleRangeChange = (range) => {
    let desde, hasta;

    if (Array.isArray(range)) {
      desde = startOfDayLocal(range[0]);
      hasta = endOfDayLocal(range[range.length - 1]);
    } else if (range instanceof Date) {
      desde = startOfDayLocal(range);
      hasta = endOfDayLocal(range);
    } else if (range.start && range.end) {
      desde = startOfDayLocal(range.start);
      hasta = endOfDayLocal(range.end);
    } else {
      const hoy = new Date();
      desde = startOfDayLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
      hasta = endOfDayLocal(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
    }

    setRangoVisible({ desde, hasta });

    if (desde < rangoCargado.desde || hasta > rangoCargado.hasta) {
      const nuevoCentro = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const nuevoDesde = startOfDayLocal(subMonths(nuevoCentro, 3));
      const nuevoHasta = endOfDayLocal(
        addMonths(new Date(nuevoCentro.getFullYear(), nuevoCentro.getMonth() + 1, 0), 3)
      );
      fetchTurnos(nuevoDesde, nuevoHasta, usuarioId);
    }
  };

  // Filtra eventos para el rango visible
  useEffect(() => {
    if (!rangoVisible.desde || !rangoVisible.hasta) return;
    setEventos(
      todosLosTurnos.filter(
        (evt) => evt.start <= rangoVisible.hasta && evt.end >= rangoVisible.desde
      )
    );
  }, [rangoVisible, todosLosTurnos]);

  /* ============== Drag & Drop ============== */
  const handleEventDrop = async ({ event, start, end }) => {
    if (!event.activo) return;

    const nuevaFecha = fechaLocalYMD(start);
    const hora_inicio =
      (`0${start.getHours()}`).slice(-2) + ":" + (`0${start.getMinutes()}`).slice(-2);
    const hora_fin =
      (`0${end.getHours()}`).slice(-2) + ":" + (`0${end.getMinutes()}`).slice(-2);

    // Solape local
    const inicio = new Date(start);
    let fin = new Date(end);
    if (fin < inicio) fin.setDate(fin.getDate() + 1);

    const haySolape = eventos.some((ev) => {
      if (ev.id === event.id) return false;
      return ev.start < fin && ev.end > inicio;
    });
    if (haySolape) {
      setMensaje("No se puede mover: se solapa con otro turno.");
      return;
    }

    try {
      const res = await fetch(`${Global.url}turnos/editar/${event.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          fecha: nuevaFecha,
          hora_inicio,
          hora_fin,
          tipo: event.tipo,
          descripcion: event.descripcion,
          plantilla_id: event.plantilla_id || null,
        }),
      });
      if (res.ok) {
        fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
        setMensaje("Turno movido correctamente.");
      } else {
        setMensaje("No se pudo mover el turno (puede haber solape).");
      }
    } catch {
      setMensaje("Error de red al mover el turno.");
    }
  };

  const handleEventResize = async ({ event, start, end }) => {
    const nuevaFecha = fechaLocalYMD(start);
    const hora_inicio =
      (`0${start.getHours()}`).slice(-2) + ":" + (`0${start.getMinutes()}`).slice(-2);
    const hora_fin =
      (`0${end.getHours()}`).slice(-2) + ":" + (`0${end.getMinutes()}`).slice(-2);

    const inicio = new Date(start);
    let fin = new Date(end);
    if (fin < inicio) fin.setDate(fin.getDate() + 1);

    const haySolape = eventos.some((ev) => {
      if (ev.id === event.id) return false;
      return ev.start < fin && ev.end > inicio;
    });
    if (haySolape) {
      setMensaje("No se puede redimensionar: se solapa con otro turno.");
      return;
    }

    try {
      const res = await fetch(`${Global.url}turnos/editar/${event.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          fecha: nuevaFecha,
          hora_inicio,
          hora_fin,
          tipo: event.tipo,
          descripcion: event.descripcion,
          plantilla_id: event.plantilla_id || null,
        }),
      });
      if (res.ok) {
        fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
        setMensaje("Turno actualizado.");
      } else {
        setMensaje("No se pudo actualizar el turno.");
      }
    } catch {
      setMensaje("Error de red al actualizar el turno.");
    }
  };

  /* ============== Selección (crear/editar) ============== */
  const handleSelectSlot = (slotInfo) => {
    setFechaSeleccionada(fechaLocalYMD(slotInfo.start)); // YMD local
    setTurnoEditar(null);
    setModalAbierto(true);
    setSlotSeleccionado({ start: slotInfo.start, end: slotInfo.end });
  };

  const handleSelectEvent = (event) => {
    setFechaSeleccionada(fechaLocalYMD(event.start)); // YMD local
    setTurnoEditar(event);
    setModalAbierto(true);
    setSlotSeleccionado(null);
  };

  /* ============== Guardar / Editar / Eliminar ============== */
  const handleSaveTurno = async (turno) => {
    // Caso plantilla: backend crea segmentos con la fecha YMD local
    if (turno.plantilla_id && turno.plantilla_id !== "personalizado") {
      const plantilla = plantillas.find((p) => String(p.id) === String(turno.plantilla_id));
      if (!plantilla || !Array.isArray(plantilla.segmentos) || plantilla.segmentos.length === 0) {
        setMensaje("Error: plantilla inválida.");
        return;
      }
      try {
        const res = await fetch(Global.url + "turnos", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            usuario_id: usuarioId,
            fecha: turno.fecha,           // YMD local
            segmentos: plantilla.segmentos,
            tipo: "turno",
            descripcion: turno.descripcion,
            plantilla_id: plantilla.id,
          }),
        });
        if (res.ok) {
          setMensaje("Turno(s) creados correctamente con plantilla.");
          handleCloseModal();
          fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
        } else {
          setMensaje("Error al crear turno(s) con plantilla.");
        }
      } catch {
        setMensaje("Error de conexión con el servidor.");
      }
      return;
    }

    // Personalizado
    const fechaYMD = turno.fecha; // ya es local
    const hInicio = turno.hora_inicio;
    const hFin = turno.hora_fin;

    // Validación de solape local
    const inicio = mkLocalDateTime(fechaYMD, hInicio);
    let fin = mkLocalDateTime(fechaYMD, hFin);
    if (fin < inicio) fin.setDate(fin.getDate() + 1);

    const esEdicion = Boolean(turnoEditar);
    const haySolape = eventos.some((ev) => {
      if (esEdicion && ev.id === turnoEditar.id) return false;
      return ev.start < fin && ev.end > inicio;
    });
    if (haySolape) {
      setMensaje("El turno se solapa con otro existente.");
      return;
    }

    try {
      if (turnoEditar) {
        const res = await fetch(`${Global.url}turnos/editar/${turnoEditar.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            fecha: fechaYMD,
            hora_inicio: hInicio,
            hora_fin: hFin,
            tipo: turno.tipo,
            descripcion: turno.descripcion,
            plantilla_id: turno.plantilla_id || null,
          }),
        });
        if (res.ok) {
          setMensaje("Turno editado correctamente.");
          handleCloseModal();
          fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
        } else {
          setMensaje("Error al editar el turno.");
        }
      } else {
        const res = await fetch(Global.url + "turnos", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            usuario_id: usuarioId,
            fecha: fechaYMD,
            segmentos: [{ hora_inicio: hInicio, hora_fin: hFin }],
            tipo: turno.tipo,
            descripcion: turno.descripcion,
            plantilla_id: turno.plantilla_id || null,
          }),
        });
        if (res.ok) {
          setMensaje("Turno creado correctamente.");
          handleCloseModal();
          fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
        } else {
          setMensaje("Error al crear el turno.");
        }
      }
    } catch {
      setMensaje("Error de conexión con el servidor.");
    }
  };

  const handleDeleteTurno = async () => {
    if (!turnoEditar) return;
    if (!window.confirm("¿Seguro que deseas eliminar este turno?")) return;
    try {
      const res = await fetch(`${Global.url}turnos/${turnoEditar.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (res.ok) {
        setMensaje("Turno eliminado correctamente.");
        handleCloseModal();
        fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
      } else {
        setMensaje("Error al eliminar el turno.");
      }
    } catch {
      setMensaje("Error de conexión con el servidor.");
    }
  };

  const handleCloseModal = () => {
    setModalAbierto(false);
    setTurnoEditar(null);
    setFechaSeleccionada(null);
    setSlotSeleccionado(null);
  };

  // Colores por plantilla/estado
  const eventPropGetter = (event) => {
    let estilo = {};
    if (event.plantilla_id) {
      const plantilla = plantillas.find((p) => String(p.id) === String(event.plantilla_id));
      if (plantilla && plantilla.color) {
        estilo.backgroundColor = plantilla.color;
        estilo.color = "#222";
      }
    }
    if (event.tipo === "ausencia") {
      estilo.backgroundColor = "#ffbaba";
      estilo.color = "#fff";
    }
    if (!event.activo) {
      estilo.backgroundColor = "#ddd";
      estilo.color = "#888";
      estilo.textDecoration = "line-through";
    }
    if (!estilo.backgroundColor) {
      estilo.backgroundColor = "#85C1E9";
      estilo.color = "#222";
    }
    return { style: estilo };
  };

  // === Modal con selector de plantilla (usa helpers robustos) ===
  const ModalTurno = ({
    show,
    onClose,
    onSave,
    turnoInicial = null,
    fechaSeleccionada = null,
    slotSeleccionado = null,
    eventos = [],
    plantillas = [],
  }) => {
    const [plantillaId, setPlantillaId] = useState("");
    const [plantillaResumen, setPlantillaResumen] = useState([]);
    const [tipo, setTipo] = useState(turnoInicial?.tipo || "turno");
    const [descripcion, setDescripcion] = useState(turnoInicial?.descripcion || "");
    const [allDay, setAllDay] = useState(turnoInicial?.allDay ? true : false);
    const [horaInicio, setHoraInicio] = useState("");
    const [horaFin, setHoraFin] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
      if (turnoInicial && turnoInicial.plantilla_id) {
        setPlantillaId(turnoInicial.plantilla_id);
        setTipo("turno");
      } else {
        setPlantillaId("");
      }
      setDescripcion(turnoInicial?.descripcion || "");
      if (turnoInicial && turnoInicial.start) {
        setHoraInicio(
          (`0${turnoInicial.start.getHours()}`).slice(-2) +
            ":" +
            (`0${turnoInicial.start.getMinutes()}`).slice(-2)
        );
        setHoraFin(
          (`0${turnoInicial.end.getHours()}`).slice(-2) +
            ":" +
            (`0${turnoInicial.end.getMinutes()}`).slice(-2)
        );
      } else if (slotSeleccionado) {
        setHoraInicio(
          (`0${slotSeleccionado.start.getHours()}`).slice(-2) +
            ":" +
            (`0${slotSeleccionado.start.getMinutes()}`).slice(-2)
        );
        setHoraFin(
          (`0${slotSeleccionado.end.getHours()}`).slice(-2) +
            ":" +
            (`0${slotSeleccionado.end.getMinutes()}`).slice(-2)
        );
      } else {
        setHoraInicio("");
        setHoraFin("");
      }
      setAllDay(false);
      setError("");
    }, [show, turnoInicial, slotSeleccionado]);

    useEffect(() => {
      const plantilla = plantillas.find((p) => String(p.id) === String(plantillaId));
      setPlantillaResumen(plantilla?.segmentos ? plantilla.segmentos : []);
    }, [plantillaId, plantillas]);

    const handleManualChange = (setter, isHora) => (e) => {
      setter(e.target.value);
      if (plantillaId) setPlantillaId("personalizado");
      if (isHora) setAllDay(false);
    };

    if (!show) return null;

    const handleSubmit = (e) => {
      e.preventDefault();

      // Plantilla
      if (plantillaId && plantillaId !== "personalizado") {
        const plantilla = plantillas.find((p) => String(p.id) === String(plantillaId));
        if (!plantilla) {
          setError("Plantilla no encontrada");
          return;
        }
        const bloques = plantilla.segmentos.map((seg) => {
          const start = mkLocalDateTime(fechaSeleccionada, seg.hora_inicio);
          let end = mkLocalDateTime(fechaSeleccionada, seg.hora_fin);
          if (seg.hora_fin < seg.hora_inicio) end.setDate(end.getDate() + 1);
          return { start, end };
        });
        const haySolape = eventos.some((ev) =>
          bloques.some((b) => ev.start < b.end && ev.end > b.start)
        );
        if (haySolape) {
          setError("Algún segmento del turno se solapa con otro existente.");
          return;
        }
        onSave({
          plantilla_id: plantilla.id,
          fecha: fechaSeleccionada,
          descripcion,
        });
        return;
      }

      // Personalizado
      let hInicio = horaInicio;
      let hFin = horaFin;
      if (allDay) {
        hInicio = "00:00";
        hFin = "23:59";
      }
      if (!hInicio || !hFin) {
        setError("Debes indicar hora de inicio y fin");
        return;
      }

      const inicio = mkLocalDateTime(fechaSeleccionada, hInicio);
      let fin = mkLocalDateTime(fechaSeleccionada, hFin);
      if (fin < inicio) fin.setDate(fin.getDate() + 1);

      const esEdicion = Boolean(turnoInicial);
      const haySolape = eventos.some((ev) => {
        if (esEdicion && ev.id === turnoInicial.id) return false;
        return ev.start < fin && ev.end > inicio;
      });
      if (haySolape) {
        setError("El turno se solapa con otro existente.");
        return;
      }

      setError("");
      onSave({
        tipo,
        descripcion,
        hora_inicio: hInicio,
        hora_fin: hFin,
        fecha: fechaSeleccionada,
        plantilla_id: plantillaId && plantillaId !== "personalizado" ? plantillaId : null,
      });
    };

    return (
      <div className="turnos-modal-backdrop">
        <div className="turnos-modal">
          <h3 style={{ marginBottom: "0.7rem" }}>
            {turnoInicial ? "Editar turno" : "Nuevo turno"}
          </h3>
          <form onSubmit={handleSubmit}>
            <label>
              Plantilla:
              <select
                value={plantillaId || ""}
                onChange={(e) => setPlantillaId(e.target.value)}
              >
                <option value="">(Sin plantilla)</option>
                {plantillas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
                <option value="personalizado">Personalizado</option>
              </select>
            </label>
            {plantillaResumen.length > 0 && (
              <div style={{ fontSize: "0.95em", marginBottom: 8 }}>
                <b>Segmentos de la plantilla:</b>{" "}
                {plantillaResumen.map((s, i) => (
                  <span key={i}>
                    {s.hora_inicio} - {s.hora_fin}
                    {i < plantillaResumen.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            )}
            <label>
              Notas/Descripción:
              <input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Notas opcionales"
              />
            </label>
            <label>
              Tipo:
              <select value={tipo} onChange={handleManualChange(setTipo, true)}>
                <option value="turno">Turno</option>
                <option value="ausencia">Ausencia</option>
              </select>
            </label>
            <label style={{ marginTop: "10px" }}>
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => {
                  setAllDay(e.target.checked);
                  setPlantillaId("personalizado");
                }}
                style={{ marginRight: "0.5em" }}
              />
              Todo el día
            </label>
            {!allDay && (
              <>
                <label>
                  Hora inicio:
                  <input
                    type="time"
                    value={horaInicio}
                    onChange={handleManualChange(setHoraInicio, true)}
                    required
                  />
                </label>
                <label>
                  Hora fin:
                  <input
                    type="time"
                    value={horaFin}
                    onChange={handleManualChange(setHoraFin, true)}
                    required
                  />
                </label>
              </>
            )}
            {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}
            <div style={{ marginTop: "1.1rem" }}>
              <button type="submit">{turnoInicial ? "Guardar cambios" : "Guardar"}</button>
              <button type="button" onClick={onClose}>
                Cancelar
              </button>
              {turnoInicial && (
                <button
                  type="button"
                  className="btn-eliminar"
                  onClick={handleDeleteTurno}  // ✅ llama a eliminar
                  style={{ marginLeft: 8, background: "#d9534f", color: "#fff" }}
                >
                  Eliminar
                </button>

              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  /* ========== Render ========== */
  const minTime = new Date(2000, 0, 1, 0, 0, 0, 0);
  const maxTime = new Date(2000, 0, 1, 23, 59, 59, 999);

  return (
    <div style={{ height: "90vh", padding: "1rem" }}>
      <h2>Gestión de turnos</h2>

      <div style={{ marginBottom: 16, maxWidth: 400 }}>
        <Select
          options={usuarios.map((u) => ({
            value: u.id,
            label: `${u.nombre} ${u.apellidos || ""}`,
          }))}
          value={
            usuarios.find((u) => u.id === usuarioId)
              ? {
                  value: usuarioId,
                  label: `${usuarios.find((u) => u.id === usuarioId).nombre} ${
                    usuarios.find((u) => u.id === usuarioId).apellidos || ""
                  }`,
                }
              : null
          }
          onChange={(selected) => setUsuarioId(selected?.value || "")}
          placeholder="Buscar o seleccionar usuario..."
          isClearable
          isSearchable
        />
      </div>

      <DragAndDropCalendar
        ref={calendarRef}
        localizer={localizer}
        events={eventos}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        defaultView="week"
        views={["day", "week", "month", "agenda"]}
        onView={setVistaActual}
        onRangeChange={handleRangeChange}
        scrollToTime={new Date(2000, 0, 1, 7, 30)}
        min={minTime}
        max={maxTime}
        popup
        selectable
        resizable
        step={15}
        timeslots={4}
        components={{ event: CustomEvent }}
        messages={{
          date: "Fecha",
          time: "Hora",
          event: "Evento",
          allDay: "Todo el día",
          week: "Semana",
          work_week: "Semana laboral",
          day: "Día",
          month: "Mes",
          previous: "Ant",
          next: "Sig",
          yesterday: "Ayer",
          tomorrow: "Mañana",
          today: "Hoy",
          agenda: "Agenda",
          showMore: (total) => `+ Ver ${total} más`,
        }}
        style={{ height: "100%" }}
        eventPropGetter={eventPropGetter}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        draggableAccessor={(event) => event.activo}
      />

      {loading && <p>Cargando turnos...</p>}
      {mensaje && <p className="rojo">{mensaje}</p>}

      <ModalTurno
        show={modalAbierto}
        onClose={handleCloseModal}
        onSave={handleSaveTurno}
        turnoInicial={turnoEditar}
        fechaSeleccionada={fechaSeleccionada}
        slotSeleccionado={slotSeleccionado}
        eventos={eventos}
        plantillas={plantillas}
      />
    </div>
  );
};
